import { HttpErrorType, HttpMethod, XhrReadyState } from './enums';
import { HttpRequestError } from './http-request-error';
import { HttpRequestOptions, HttpResponse } from './models';

export class HttpRequest {

    private body: string;
    private readonly headers: { [key: string]: string | string[] } = {};
    private readonly url: string;
    private readonly method: HttpMethod;
    private readonly xhr: XMLHttpRequest;
    private readonly options: HttpRequestOptions;

    constructor(method: HttpMethod, url: string, options?: HttpRequestOptions) {
        this.method = method;
        this.url = url;
        this.xhr = new XMLHttpRequest();
        this.xhr.open(this.method, this.url, true);
        this.options = options || {timeout: 60000};
    }

    setHeader(name: string, value: string | string[]): void {
        this.headers[name] = value;
        if (typeof value === 'string') {
            this.xhr.setRequestHeader(name, value);
        } else {
            value.forEach(v => this.xhr.setRequestHeader(name, v));
        }
    }

    abort(): void {
        this.xhr.abort();
    }

    setBody(body: string): void {
        this.body = body;
    }

    async sendAsync(body?: string): Promise<HttpResponse> {
        if (body) {
            this.setBody(body);
        }

        return new Promise<HttpResponse>((resolve, reject) => {
            const response: HttpResponse = {
                headers: {},
                body: '',
                statusCode: 0,
                isSuccessStatusCode: false,
                xhr: this.xhr,
            };

            this.xhr.onabort = () => {
                const error: HttpRequestError = new HttpRequestError();
                error.type = HttpErrorType.Aborted;
                error.message = 'XMLHttpRequest aborted';
                error.request = this;
                reject(error);
            };

            this.xhr.onerror = () => {
                const error: HttpRequestError = new HttpRequestError();
                error.type = HttpErrorType.UnknownError;
                error.message = 'XMLHttpRequest failed';
                error.request = this;
                reject(error);
            };

            this.xhr.onreadystatechange = () => {
                if (this.xhr.readyState === XhrReadyState.HeadersReceived) {
                    const headers: string = this.xhr.getAllResponseHeaders();
                    const list: string[] = headers.trim().split(/[\r\n]+/);
                    const map: { [key: string]: string } = {};
                    list.forEach((line: string) => {
                        const tokens: string[] = line.split(': ');
                        const name: string = tokens.shift();
                        map[name] = tokens.join(': ');
                    });
                    response.headers = map;
                    response.statusCode = this.xhr.status;
                }
                if (this.xhr.readyState === XhrReadyState.Done) {
                    // readystate ==  4 + status == 0 =>
                    // ERR_CONNECTION_REFUSED etc
                    if (!this.xhr.status) {
                        const error: HttpRequestError = new HttpRequestError();
                        error.message = 'XMLHttpRequest failed. Status is 0, the request might have been cancelled by the agent.';
                        error.status = this.xhr.status;
                        error.request = this;
                        error.type = HttpErrorType.UnknownError;
                        reject(error);
                    } else {
                        response.statusCode = this.xhr.status;
                        response.body = this.xhr.responseText;
                        response.isSuccessStatusCode = response.statusCode >= 200 && response.statusCode < 400;
                        resolve(response);
                    }
                }
            };
            this.xhr.send(this.body);
        });
    }
}
